<template>
  <div class="h-full w-full flex flex-col">
    <div v-if="editor" class="border-b w-full p-1">
      <div class="-mx-1">
        <div class="flex items-center flex-wrap">
          <template v-for="(group, groupIndex) in menu" :key="`group-${groupIndex}`">
            <div
              v-if="!group.mark || editor.isActive(group.mark)"

              class="flex items-center space-x-1 px-1 border-r last:border-none"
            >
              <EditorButton
                v-for="(button, buttonIndex) in group"
                :key="`group-${groupIndex}-button-${buttonIndex}`"
                :editor="editor"
                :button="button"
              />
            </div>
          </template>
        </div>
      </div>
    </div>

    <o-scroll class="flex-grow flex-shrink h-32 bg-white dark:bg-gray-950">
      <div id="editor" class="h-full p-2 bg-white dark:bg-gray-900/40">
        <editor-content :editor="editor" />
      </div>
    </o-scroll>
  </div>
</template>

<script setup lang="ts">
import { Editor, EditorContent } from '@tiptap/vue-3'
import TextAlign from '@tiptap/extension-text-align'
import Link from '@tiptap/extension-link'
import Mention from '@tiptap/extension-mention'
import createSuggestion from './suggestion'
import EditorButton from '@/components/elements/editor/Button.vue'
import { tiptapStarterExtensions } from '@/lib/tiptapStarterExtensions'

interface Props {
  modelValue?: string
  placeholders?: any[]
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: '',
  placeholders: () => [],
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void
}>()

const editor = ref<any>(null)

const menu = [
  [
    {
      name: 'Bold',
      icon: 'bold',
      mark: 'bold',
      command: 'toggleBold',
    },
    {
      name: 'Italic',
      icon: 'italic',
      mark: 'italic',
      command: 'toggleItalic',
    },
    {
      name: 'Underline',
      icon: 'underline',
      mark: 'underline',
      command: 'toggleUnderline',
    },
    {
      name: 'Strike',
      icon: 'strikethrough',
      mark: 'strike',
      command: 'toggleStrike',
    },
    {
      name: 'Link',
      icon: 'link',
      mark: 'link',
      command: 'toggleLink',
    },
    {
      name: 'Insert Placeholer',
      icon: 'braces',
      mark: 'placeholder',
      command: 'insertContent',
    },
  ],
  [
    {
      name: 'Align text left',
      icon: 'alignLeft',
      command: 'setTextAlign',
      mark: { textAlign: 'left' },
      params: 'left',
    },
    {
      name: 'Align text center',
      icon: 'alignCenter',
      command: 'setTextAlign',
      mark: { textAlign: 'center' },
      params: 'center',
    },
    {
      name: 'Align text right',
      icon: 'alignRight',
      command: 'setTextAlign',
      mark: { textAlign: 'right' },
      params: 'right',
    },
    {
      name: 'Justify text',
      icon: 'alignJustify',
      command: 'setTextAlign',
      mark: { textAlign: 'justify' },
      params: 'justify',
    },
  ],
  [
    {
      name: 'Paragraph',
      icon: 'paragraph',
      mark: 'paragraph',
      command: 'setParagraph',
    },
    {
      name: 'Heading 1',
      icon: 'h1',
      mark: 'heading',
      command: 'toggleHeading',
      params: { level: 1 },
    },
    {
      name: 'Heading 2',
      icon: 'h2',
      mark: 'heading',
      command: 'toggleHeading',
      params: { level: 2 },
    },
    {
      name: 'Heading 3',
      icon: 'h3',
      mark: 'heading',
      command: 'toggleHeading',
      params: { level: 3 },
    },
  ],
  [
    {
      name: 'Bullet list',
      icon: 'bulletList',
      mark: 'bulletList',
      command: 'toggleBulletList',
    },
    {
      name: 'Ordered list',
      icon: 'orderedList',
      mark: 'orderedList',
      command: 'toggleOrderedList',
    },
  ],
  [
    {
      name: 'Undo',
      icon: 'editorUndo',
      command: 'undo',
    },
    {
      name: 'Redo',
      icon: 'editorRedo',
      command: 'redo',
    },
  ],
]

const mentionItems = computed(() => {
  if (props.placeholders && props.placeholders.length) {
    return props.placeholders
      .filter(value => typeof value === 'string')
      .map(value => ({ id: value, label: value, value }))
  } else {
    return []
  }
})

watch(() => props.modelValue, (value) => {
  if (!editor.value) {
    return
  }

  const isSame = editor.value.getHTML() === value

  if (isSame) {
    return
  }

  editor.value.commands.setContent(value, false)
})

onMounted(() => {
  editor.value = new Editor({
    content: props.modelValue,
    extensions: [
      ...tiptapStarterExtensions(),
      Mention.configure({
        name: 'placeholder',
        deleteTriggerWithBackspace: true,
        renderText ({ options, node }: any) {
          return `{{ ${node.attrs.id} }}`
        },
        renderHTML ({ options, node }: any) {
          return [
            'span',
            { 'data-type': options.name, 'data-id': node.attrs.id },
            `{{ ${node.attrs.id} }}`,
          ]
        },
        suggestion: createSuggestion({
          getItems: ({ query }: any) => {
            const q = (query || '').toLowerCase()
            const items = mentionItems.value
            return items
              .filter(item => (
                (item.label && item.label.toLowerCase().includes(q)) ||
                (item.id && item.id.toLowerCase().includes(q)) ||
                (item.value && item.value.toLowerCase().includes(q))
              ))
          },
        }),
      }).extend({
        name: 'placeholder'
      }),
      Link.configure({
        protocols: ['ftp', 'mailto'],
        openOnClick: false,
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
    ],
    onUpdate: () => {
      emit('update:modelValue', editor.value.getHTML())
    },
  })

  editor.value.chain().focus()
})

onBeforeUnmount(() => {
  editor.value?.destroy()
})
</script>

<style>
@reference "../../../assets/css/main.css";

#editor {
  .tiptap>*+* {
    margin-top: .75em;
    margin-bottom: .75em
  }

  a {
    @apply text-blue-700 underline;
  }
  p {
    @apply my-1;
  }

  blockquote,
  figure {
    @apply my-1 mx-10;
  }

  hr {
    @apply border;
  }

  h1 {
    @apply text-4xl font-bold my-2;
  }

  h2 {
    @apply text-2xl font-bold my-3;
  }

  h3 {
    @apply text-lg font-bold my-1;
  }

  h4 {
    @apply text-base font-bold my-5;
  }

  h5 {
    @apply text-sm font-bold my-6;
  }

  h6 {
    @apply text-xs font-bold my-10;
  }

  article,
  aside,
  nav,
  section {
    h1 {
      @apply text-2xl font-bold my-3;
    }

    article,
    aside,
    nav,
    section {
      h1 {
        @apply text-lg font-bold my-1;
      }

      article,
      aside,
      nav,
      section {
        h1 {
          @apply text-base font-bold my-5;
        }

        article,
        aside,
        nav,
        section {
          h1 {
            @apply text-sm font-bold my-6;
          }

          article,
          aside,
          nav,
          section {
            h1 {
              @apply text-xs font-bold my-10;
            }
          }
        }
      }
    }
  }

  ul,
  menu {
    /* Should be my-1 for a full reset, but that spacing is too large here. */
    @apply list-disc my-1 pl-10;
  }

  ol {
    @apply list-decimal my-1 pl-10;
  }

  ul,
  ol {
    ul {
      list-style-type: circle;
    }

    ul,
    ol {
      ul {
        list-style-type: square;
      }
    }
  }

  dd {
    @apply pl-10;
  }

  dl {
    @apply my-1;
  }

  ul,
  ol,
  menu,
  dl {
    ul,
    ol,
    menu,
    dl {
      margin: 0;
    }
  }

  legend {
    @apply py-0 px-1;
  }

  fieldset {
    @apply my-0 mx-1 pt-0 px-1 pb-2;
  }

  b,
  strong {
    font-weight: bold;
  }

  pre {
    @apply my-1;
  }

  img {
    @apply my-1 mx-auto rounded-lg border w-full max-w-2xl;
  }
}

.dark #editor {
  .ProseMirror {
    table {
      td,
      th {
        border-color: var(--color-gray-800);
      }

      th {
        background-color: var(--color-gray-900);
      }

      .selectedCell:after {
        background: rgba(200, 200, 255, 0.4);
      }
    }
  }
}

.tiptap {
  :first-child {
    margin-top: 0;
  }

  [data-type="placeholder"] {
    @apply bg-primary-100/80 dark:bg-primary-800/40 text-primary-900 dark:text-primary-100;
    border-radius: 0.4rem;
    box-decoration-break: clone;
    padding: 0.1rem 0.3rem;
  }
}
</style>
